"use client";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

interface SidebarProps {
  data: {
    link: [
      {
       link: string;
       text: string;
      }
    ]
  };
}

export default function Sidebar({ data }: SidebarProps) {
  const [pathname, setPathname] = useState<string>("");
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setPathname(window.location.pathname);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const sidebarElement = sidebarRef.current;
    const footerElement = document.getElementById("footer");

    if (!sidebarElement || !footerElement) {
      return;
    }

    let animationFrameId: number | null = null;

    const updateSidebarOffset = () => {
      animationFrameId = null;

      const viewportWidth = window.innerWidth;
      if (viewportWidth <= 1024) {
        sidebarElement.style.transform = "";
        return;
      }

      const footerRect = footerElement.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (footerRect.top >= viewportHeight) {
        sidebarElement.style.transform = "translateY(0px)";
        return;
      }

      const totalDistance = footerRect.height + viewportHeight || 1;
      const progress = Math.min(
        Math.max((viewportHeight - footerRect.top) / totalDistance, 0),
        1
      );
      const translateY = -progress * viewportHeight;
      sidebarElement.style.transform = `translateY(${translateY}px)`;
    };

    const scheduleUpdate = () => {
      if (animationFrameId === null) {
        animationFrameId = window.requestAnimationFrame(updateSidebarOffset);
      }
    };

    sidebarElement.style.willChange = "transform";
    updateSidebarOffset();

    const passiveOptions: AddEventListenerOptions = { passive: true };
    window.addEventListener("scroll", scheduleUpdate, passiveOptions);
    window.addEventListener("resize", scheduleUpdate);

    return () => {
      if (animationFrameId !== null) {
        window.cancelAnimationFrame(animationFrameId);
      }
      window.removeEventListener("scroll", scheduleUpdate, passiveOptions);
      window.removeEventListener("resize", scheduleUpdate);
      sidebarElement.style.transform = "";
      sidebarElement.style.willChange = "";
    };
  }, []);
  return (
    <div
      ref={sidebarRef}
      className="hidden h-screen dt:block fixed left-1 top-0"
      id="Sidebar"
    >
      <div className="top-1/2 -translate-y-1/2 absolute w-max">
      {data.link.map((link) => (
        <Link href={link.link} key={link.text} className={`font-bold uppercase block text-1.5 tracking-wider leading-1.5 ${pathname === link.link ? 'text-red' : ' hover:text-blue'}`}>
          {link.text}
        </Link>
      ))}
      </div>
    </div>
  );
}
